import { ErrorMessage, Field, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Button, Card, CardBody, Col, Form, Label, Row } from "reactstrap";
import SimpleBar from "simplebar-react";
import {
  initialValues,
  populateForm,
  QuestionDifficulty,
  QuestionLevel,
  QuestionTopic,
  questionTypePicMap,
  validationSchema,
} from "../../constants/QuestionsBankConstants";
import {
  createQuestion,
  fetchQuestion,
  resetQuestion,
  updateQuestion,
} from "../../store/questionBank/action";
import "./AnswersCard.scss";

const AnswersCard = ({
  id,
  setSelectedCardId,
  isEditable,
  handleEditClick,
}) => {
  const dispatch = useDispatch();
  const question = useSelector((state) => state?.QuestionBankReducer?.question);
  const [questionInitialValues, setQuestionInitialValues] = useState(
    populateForm(initialValues)
  );

  useEffect(() => {
    if (id) {
      dispatch(fetchQuestion({ id: id }));
    } else {
      dispatch(resetQuestion());
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      setQuestionInitialValues(populateForm(question));
    } else {
      setQuestionInitialValues(populateForm(initialValues));
    }
  }, [id, question]);

  const handleSubmit = (values) => {
    let answersData;

    if (values.questiontype === "MCQ") {
      if (
        values.answers[0]?.Options?.length < 3 ||
        !values.answers[0]?.Answers?.length
      ) {
        toast.error("MCQ requires at least 3 options and 1 answer.");
        return;
      }

      // Filter out empty answers
      const filteredAnswers = values.answers[0].Answers.filter(
        (ans) => ans.trim() !== ""
      );

      answersData = [
        {
          Options: values.answers[0].Options || [],
          Answers: filteredAnswers,
        },
      ];
    } else {
      // For other types like Descriptive, we just include Answers
      answersData = [
        {
          Answers: values.answers[0].Answers[0]
            ? [values.answers[0].Answers[0]]
            : [],
        },
      ];
    }

    const formattedData = {
      questionTitle: values.questionTitle,
      difficulty: values.difficulty,
      level: values.level,
      topic: values.topic,
      questiontype: values.questiontype,
      answers: answersData,
    };

    if (id) {
      // Update
      dispatch(updateQuestion({ id: id, data: formattedData }));
      handleEditClick(id, false);
      setSelectedCardId(null);
    } else {
      // Create
      dispatch(createQuestion({ data: formattedData }));
      handleEditClick(id, false);
      setSelectedCardId(null);
    }
  };

  return (
    <Card
      className="d-flex justify-content-center align-items-top"
      style={{
        backgroundColor: "#F9FCFF",
        borderColor: "#7AC7FF",
        borderWidth: "2px",
        borderStyle: "solid",
        borderRadius: "8px",
      }}
    >
      <CardBody style={{ width: "100%", maxWidth: "800px" }}>
        <SimpleBar style={{ maxHeight: "700px", overflowY: "auto" }}>
          <div className="px-3 py-2">
            <Formik
              initialValues={questionInitialValues}
              enableReinitialize={true}
              validationSchema={validationSchema}
            >
              {({ values, setFieldValue, errors, touched, resetForm }) => {
                return (
                  <Form className="text-left">
                    <div className="d-flex justify-content-between align-items-baseline mb-3">
                      <h5>{id ? "Edit Question" : "New Question"}</h5>
                      <div className="d-flex justify-content-end align-items-start gap-2">
                        <Button
                          type="button"
                          style={{
                            backgroundColor: "#fff",
                            border: "1px solid #C3D3E2",
                            color: "#000",
                            padding: "8px 20px",
                            borderRadius: "8px",
                          }}
                          onClick={() => {
                            handleEditClick(null, true);
                            setSelectedCardId(null);
                            setQuestionInitialValues(
                              populateForm(initialValues)
                            );
                            resetForm();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          disabled={!isEditable[id]}
                          type="button"
                          style={{
                            backgroundColor: "#00B91F",
                            border: "1px solid #C3D3E2",
                            color: "#fff",
                            padding: "8px 20px",
                            borderRadius: "8px",
                          }}
                          onClick={() => {
                            handleSubmit(values);
                            resetForm();
                          }}
                        >
                          {id ? "Update" : "Save"}
                        </Button>
                      </div>
                    </div>

                    <Row className="mb-3">
                      <Col lg={12}>
                        <div className="d-flex flex-column gap-1">
                          {/* Question */}
                          <div>
                            <span>Enter Question</span>
                            <span className="text-danger">*</span>
                          </div>
                          <Field
                            name="questionTitle"
                            className={`form-control ${
                              errors.questionTitle && touched.questionTitle
                                ? "is-invalid"
                                : ""
                            }`}
                            as="textarea"
                            disabled={!isEditable[id]}
                            placeholder="Enter"
                          />
                          {errors?.questionTitle && touched?.questionTitle && (
                            <div className="invalid-feedback">
                              {errors?.questionTitle}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col lg={6}>
                        <div className="d-flex flex-column gap-1">
                          <div>
                            <span>Topic</span>
                            <span className="text-danger">*</span>
                          </div>
                          <Field
                            name="topic"
                            as="select"
                            className={`form-select ${
                              errors.topic && touched.topic ? "is-invalid" : ""
                            }`}
                            disabled={!isEditable[id]}
                          >
                            <option value="">Select</option>
                            {Object.keys(QuestionTopic).map((topic) => (
                              <option
                                key={topic}
                                onClick={() => {
                                  setFieldValue("topic", topic);
                                }}
                              >
                                {topic}
                              </option>
                            ))}
                          </Field>
                          {errors.topic && touched.topic && (
                            <div className="invalid-feedback">
                              {errors.topic}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="d-flex flex-column gap-1">
                          <div>
                            <span>Type</span>
                            <span className="text-danger">*</span>
                          </div>
                          <Field
                            name="questiontype"
                            as="select"
                            className={`form-select ${
                              errors.questiontype && touched.questiontype
                                ? "is-invalid"
                                : ""
                            }`}
                            disabled={!isEditable[id]}
                          >
                            <option value="">Select</option>
                            {Object.keys(questionTypePicMap).map((type) => (
                              <option
                                key={type}
                                onClick={() => setFieldValue("type", type)}
                              >
                                {type}
                              </option>
                            ))}
                          </Field>
                          {errors.questiontype && touched.questiontype && (
                            <div className="invalid-feedback">
                              {errors.questiontype}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col>
                        <div className="d-flex flex-column gap-1">
                          <div>
                            <span>Difficulty</span>
                            <span className="text-danger">*</span>
                          </div>
                          <Field
                            name="difficulty"
                            as="select"
                            className={`form-select ${
                              errors.difficulty && touched.difficulty
                                ? "is-invalid"
                                : ""
                            }`}
                            disabled={!isEditable[id]}
                          >
                            <option value="">Select</option>
                            {Object.keys(QuestionDifficulty).map(
                              (difficulty) => (
                                <option
                                  key={difficulty}
                                  onClick={() =>
                                    setFieldValue("difficulty", difficulty)
                                  }
                                >
                                  {difficulty}
                                </option>
                              )
                            )}
                          </Field>
                          {errors.difficulty && touched.difficulty && (
                            <div className="invalid-feedback">
                              {errors.difficulty}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col>
                        <div className="d-flex flex-column gap-1">
                          <div>
                            <span>Level</span>
                            <span className="text-danger">*</span>
                          </div>
                          <Field
                            name="level"
                            as="select"
                            className={`form-select ${
                              errors.level && touched.level ? "is-invalid" : ""
                            }`}
                            disabled={!isEditable[id]}
                          >
                            <option value="">Select</option>
                            {Object.keys(QuestionLevel).map((level) => (
                              <option
                                key={level}
                                onClick={() => setFieldValue("level", level)}
                              >
                                {level}
                              </option>
                            ))}
                          </Field>
                          {errors.level && touched.level && (
                            <div className="invalid-feedback">
                              {errors.level}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      {/* Answer */}
                      <Col lg={12}>
                        <div className="col-12">
                          {values.questiontype === "MCQ" ? (
                            // MCQ
                            <div>
                              {values.answers[0]?.Options?.map(
                                (option, index) => (
                                  <div key={index} className="mb-3">
                                    {/* Row for Answer Label and Mark Button */}
                                    <div className="d-flex align-items-center mb-1 justify-content-between">
                                      <Label>Answer {index + 1}</Label>
                                      <span
                                        style={{ fontSize: "12px" }}
                                        className={`text-primary cursor-pointer d-flex flex-row align-items-center ${
                                          values.answers[0]?.Answers?.includes(
                                            option
                                          )
                                            ? "text-success mdi mdi-check"
                                            : "text-dark"
                                        }`}
                                        onClick={() => {
                                          if (!isEditable[id]) return;
                                          const isMarked =
                                            values.answers[0]?.Answers?.includes(
                                              option
                                            );
                                          const newAnswers = isMarked
                                            ? values.answers[0]?.Answers.filter(
                                                (answer) => answer !== option
                                              )
                                            : [
                                                ...(values.answers[0]
                                                  ?.Answers || []),
                                                option,
                                              ];
                                          setFieldValue(
                                            "answers[0].Answers",
                                            newAnswers
                                          );
                                        }}
                                      >
                                        {values.answers[0]?.Answers?.includes(
                                          option
                                        )
                                          ? "Marked"
                                          : "Mark"}
                                      </span>
                                    </div>
                                    <ErrorMessage
                                      name={`answers[0].Options[${index}]`}
                                      component="div"
                                      className="text-danger"
                                    />
                                    <Field
                                      name={`answers[0].Options[${index}]`}
                                      className="form-control"
                                      as="input"
                                      disabled={!isEditable[id]}
                                      placeholder="Enter Option"
                                    />
                                  </div>
                                )
                              )}

                              {/* Add/Remove Option Buttons */}
                              <div className="mt-2">
                                <Button
                                  type="button"
                                  style={{
                                    backgroundColor: "#3F88E4",
                                    border: "1px solid #3F88E4",
                                    color: "#fff",
                                    borderRadius: "8px",
                                    padding: "4px 20px",
                                  }}
                                  onClick={() => {
                                    if (!isEditable[id]) return;
                                    const newOptions = [
                                      ...(values.answers[0]?.Options || []),
                                      "",
                                    ];
                                    setFieldValue(
                                      "answers[0].Options",
                                      newOptions
                                    );
                                  }}
                                >
                                  Add
                                </Button>
                                <Button
                                  type="button"
                                  className="ms-2"
                                  style={{
                                    backgroundColor: "#fff",
                                    border: "1px solid #C3D3E2",
                                    color: "#000",
                                    borderRadius: "8px",
                                    padding: "4px 10px",
                                  }}
                                  onClick={() => {
                                    if (!isEditable[id]) return;
                                    const newOptions =
                                      values.answers[0]?.Options?.slice(0, -1);
                                    setFieldValue(
                                      "answers[0].Options",
                                      newOptions
                                    );
                                  }}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ) : values.questiontype === "True/False" ? (
                            // T/F

                            <div>
                              <Label>Answer</Label>
                              <div className="form-check">
                                <Label>
                                  <Field
                                    type="radio"
                                    name="answer"
                                    value="True"
                                    checked={
                                      values.answers[0]?.Answers[0] === "True"
                                    }
                                    onChange={() =>
                                      setFieldValue(
                                        "answers[0].Answers[0]",
                                        "True"
                                      )
                                    }
                                    disabled={!isEditable[id]}
                                  />
                                  True
                                </Label>
                              </div>
                              <div className="form-check">
                                <Label>
                                  <Field
                                    type="radio"
                                    name="answer"
                                    value="False"
                                    checked={
                                      values.answers[0]?.Answers[0] === "False"
                                    }
                                    onChange={() =>
                                      setFieldValue(
                                        "answers[0].Answers[0]",
                                        "False"
                                      )
                                    }
                                    disabled={!isEditable[id]}
                                  />
                                  False
                                </Label>
                              </div>
                            </div>
                          ) : (
                            // Others
                            <div>
                              <Label>Answer</Label>
                              <br />
                              <ErrorMessage
                                name="answers[0].Answers[0]"
                                component="span"
                                className="text-danger"
                              />
                              <Field
                                name="answers[0].Answers[0]"
                                className="form-control my-2"
                                style={{ height: "100px", resize: "none" }}
                                as="textarea"
                                placeholder="Enter Answer"
                                disabled={!isEditable[id]}
                              />
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </SimpleBar>
      </CardBody>
    </Card>
  );
};

export default AnswersCard;
